import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  Output,
} from '@angular/core';
import type { StreetAddress } from '@essent/new-customer';
import { ConsumptionValuesVM } from '@innogy/become-a-customer/shared/interfaces';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { type EnvConfigBrand, EnvironmentConfig } from '@core/config-models';
import { CrntIcon } from '@essent/current-web-angular';
import {
  CSCaptureDirective,
  CSMaskDirective,
} from '@legacy/utils-contentsquare';
import { AutomationIdDirective } from '@innogy/utils-automation-id';

import {
  getAddressString,
  getConsumptionIconLabels,
  type CrntIconLabel,
} from '../utils';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'wl-commodity-offer-calculation-based-on',
  templateUrl: './commodity-offer-calculation-based-on.component.html',
  styleUrls: [
    './commodity-offer-calculation-based-on.component.ed.scss',
    './commodity-offer-calculation-based-on.component.essent.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    CrntIcon,
    AutomationIdDirective,
    CSCaptureDirective,
    CSMaskDirective,
  ],
})
export class CommodityOfferCalculationBasedOnComponent
  implements OnInit, OnChanges
{
  @HostBinding('attr.variant')
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  variant?: 'wide' | 'narrow' = 'wide';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() label?: string;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() electricityLabel?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() gasLabel?: string;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() address?: Partial<StreetAddress>; // Partial to account for address not found in BAC

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() consumptions?: ConsumptionValuesVM;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() adjustCalculationLabel?: string;

  @Output()
  adjustCalculation = new EventEmitter<void>();

  addressString?: string;
  consumptionIconLabels?: CrntIconLabel[];
  brand: EnvConfigBrand;

  constructor(
    @Inject(ENVIRONMENT_CONFIG) private readonly envConfig: EnvironmentConfig
  ) {
    this.brand = this.envConfig.brand;
  }

  ngOnInit() {
    this.updateValues(this.address, this.consumptions);
  }

  // onChanges, as address and consumption can change after this component was init
  ngOnChanges(changes: SimpleChanges): void {
    const newAddress = changes['address']?.currentValue;
    const oldAddress = changes['address']?.previousValue;
    const newConsumptions = changes['consumptions']?.currentValue;
    const oldConsumptions = changes['consumptions']?.previousValue;

    if (newAddress !== oldAddress) {
      this.updateValues(newAddress, oldConsumptions);
    }

    if (newConsumptions !== oldConsumptions) {
      this.updateValues(oldAddress, newConsumptions);
    }
  }

  updateValues(
    address?: Partial<StreetAddress>,
    consumptions?: ConsumptionValuesVM
  ) {
    if (address) {
      this.addressString = getAddressString(address);
    }

    if (consumptions) {
      this.consumptionIconLabels = getConsumptionIconLabels(
        consumptions,
        this.electricityLabel || '',
        this.gasLabel || ''
      );
    }
  }
}
